import React, { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { Drawer } from '../../components/ui/Drawer';
import { StatusPill } from '../../components/ui/StatusPill';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, LifeBuoyIcon, MailIcon, PhoneIcon } from 'lucide-react';
const faqs = [{
  q: 'How do I acknowledge a purchase order?',
  a: 'Navigate to the Orders page, select the pending PO, and click the "Acknowledge Order" button in the action bar.'
}, {
  q: 'How do I create an invoice from a PO?',
  a: 'Go to the Invoices page and click "Create Invoice". Select the relevant PO from the dropdown, and the line items will auto-populate.'
}, {
  q: 'How do I update my bank details?',
  a: 'Go to Company Profile > Banking tab, and click "Request Bank Change". Note that this requires maker-checker approval from FPG.'
}, {
  q: 'What documents are required for onboarding?',
  a: 'Standard requirements include Business Registration, Tax Certificate, and Bank Statement. Specific categories may require additional quality or halal certifications.'
}];
const cases = [{
  id: 'CS-2026-001',
  subject: 'Invoice payment inquiry',
  category: 'Finance',
  status: 'Open',
  created: 'Mar 12, 2026',
  updated: 'Mar 13, 2026'
}, {
  id: 'CS-2025-045',
  subject: 'Portal access issue',
  category: 'Technical',
  status: 'Resolved',
  created: 'Feb 28, 2026',
  updated: 'Mar 01, 2026'
}];
export const HelpSupportPage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(0);
  const [isRaiseCaseOpen, setIsRaiseCaseOpen] = useState(false);
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-1158">
      <PageHeader title="Help & Support" breadcrumbs={[{
      label: 'Home',
      href: '/supplier/dashboard'
    }, {
      label: 'Help & Support'
    }]} primaryAction={<Button leftIcon={<LifeBuoyIcon className="w-4 h-4" data-id="element-1161" />} onClick={() => setIsRaiseCaseOpen(true)} data-id="element-1160">
            Raise Case
          </Button>} data-id="element-1159" />

      <div className="relative max-w-2xl mx-auto mb-8" data-id="element-1162">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" data-id="element-1163" />
        <input type="text" placeholder="Search help articles, FAQs, or case IDs..." className="w-full pl-12 pr-4 py-4 text-base border border-border-default rounded-fluent-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-fpg-navy" data-id="element-1164" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1165">
        <div className="lg:col-span-2 space-y-6" data-id="element-1166">
          <Card data-id="element-1167">
            <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-1168">
              Frequently Asked Questions
            </h2>
            <div className="space-y-2" data-id="element-1169">
              {faqs.map((faq, idx) => <div key={idx} className="border border-border-light rounded-fluent overflow-hidden" data-id="element-1170">
                  <button className="w-full px-4 py-3 flex justify-between items-center bg-surface-secondary hover:bg-gray-100 transition-colors text-left" onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} data-id="element-1171">
                    <span className="font-medium text-text-primary" data-id="element-1172">
                      {faq.q}
                    </span>
                    {expandedFaq === idx ? <ChevronUpIcon className="w-4 h-4 text-text-secondary" data-id="element-1173" /> : <ChevronDownIcon className="w-4 h-4 text-text-secondary" data-id="element-1174" />}
                  </button>
                  {expandedFaq === idx && <div className="px-4 py-3 bg-surface-primary text-sm text-text-secondary border-t border-border-light" data-id="element-1175">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </Card>

          <Card data-id="element-1176">
            <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-1177">
              My Support Cases
            </h2>
            <DataTable data={cases} columns={[{
            header: 'Case ID',
            accessorKey: 'id',
            cell: row => <span className="font-medium text-fpg-navy" data-id="element-1179">{row.id}</span>
          }, {
            header: 'Subject',
            accessorKey: 'subject'
          }, {
            header: 'Category',
            accessorKey: 'category'
          }, {
            header: 'Status',
            cell: row => <StatusPill status={row.status} data-id="element-1180" />
          }, {
            header: 'Last Updated',
            accessorKey: 'updated'
          }]} data-id="element-1178" />
          </Card>
        </div>

        <div className="space-y-6" data-id="element-1181">
          <Card data-id="element-1182">
            <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-1183">
              Who to Contact
            </h2>
            <div className="space-y-4" data-id="element-1184">
              <div className="p-3 border border-border-light rounded-fluent bg-surface-secondary" data-id="element-1185">
                <h3 className="font-medium text-text-primary mb-1" data-id="element-1186">
                  Procurement
                </h3>
                <p className="text-sm text-text-secondary mb-2" data-id="element-1187">Rachel Tan</p>
                <div className="flex items-center gap-2 text-sm text-fpg-navy" data-id="element-1188">
                  <MailIcon className="w-4 h-4" data-id="element-1189" /> rachel.tan@fairprice.sg
                </div>
              </div>
              <div className="p-3 border border-border-light rounded-fluent bg-surface-secondary" data-id="element-1190">
                <h3 className="font-medium text-text-primary mb-1" data-id="element-1191">
                  Finance / AP
                </h3>
                <p className="text-sm text-text-secondary mb-2" data-id="element-1192">Sarah Lim</p>
                <div className="flex items-center gap-2 text-sm text-fpg-navy" data-id="element-1193">
                  <MailIcon className="w-4 h-4" data-id="element-1194" /> sarah.lim@fairprice.sg
                </div>
              </div>
              <div className="p-3 border border-border-light rounded-fluent bg-surface-secondary" data-id="element-1195">
                <h3 className="font-medium text-text-primary mb-1" data-id="element-1196">
                  Technical Support
                </h3>
                <p className="text-sm text-text-secondary mb-2" data-id="element-1197">
                  Portal Helpdesk
                </p>
                <div className="flex items-center gap-2 text-sm text-fpg-navy" data-id="element-1198">
                  <MailIcon className="w-4 h-4" data-id="element-1199" /> support@fairprice.sg
                </div>
                <div className="flex items-center gap-2 text-sm text-fpg-navy mt-1" data-id="element-1200">
                  <PhoneIcon className="w-4 h-4" data-id="element-1201" /> +65 6123 4567
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Drawer isOpen={isRaiseCaseOpen} onClose={() => setIsRaiseCaseOpen(false)} title="Raise Support Case" size="md" footer={<>
            <Button variant="ghost" onClick={() => setIsRaiseCaseOpen(false)} data-id="element-1203">
              Cancel
            </Button>
            <Button variant="primary" data-id="element-1204">Submit Case</Button>
          </>} data-id="element-1202">
        <div className="space-y-4" data-id="element-1205">
          <div data-id="element-1206">
            <label className="block text-sm font-medium text-text-primary mb-1" data-id="element-1207">
              Category
            </label>
            <select className="w-full p-2 border border-border-default rounded-fluent text-sm bg-white" data-id="element-1208">
              <option data-id="element-1209">Finance & Payments</option>
              <option data-id="element-1210">Technical Issue</option>
              <option data-id="element-1211">Procurement & Orders</option>
              <option data-id="element-1212">Legal & Contracts</option>
              <option data-id="element-1213">Other</option>
            </select>
          </div>
          <div data-id="element-1214">
            <label className="block text-sm font-medium text-text-primary mb-1" data-id="element-1215">
              Priority
            </label>
            <select className="w-full p-2 border border-border-default rounded-fluent text-sm bg-white" data-id="element-1216">
              <option data-id="element-1217">Low</option>
              <option data-id="element-1218">Medium</option>
              <option data-id="element-1219">High</option>
            </select>
          </div>
          <div data-id="element-1220">
            <label className="block text-sm font-medium text-text-primary mb-1" data-id="element-1221">
              Subject
            </label>
            <input type="text" className="w-full p-2 border border-border-default rounded-fluent text-sm" placeholder="Brief description of the issue" data-id="element-1222" />
          </div>
          <div data-id="element-1223">
            <label className="block text-sm font-medium text-text-primary mb-1" data-id="element-1224">
              Related Object (Optional)
            </label>
            <input type="text" className="w-full p-2 border border-border-default rounded-fluent text-sm" placeholder="e.g., PO-2026-00142 or INV-2026-00089" data-id="element-1225" />
          </div>
          <div data-id="element-1226">
            <label className="block text-sm font-medium text-text-primary mb-1" data-id="element-1227">
              Description
            </label>
            <textarea className="w-full p-2 border border-border-default rounded-fluent text-sm" rows={5} placeholder="Provide detailed information..." data-id="element-1228"></textarea>
          </div>
          <Button variant="secondary" leftIcon={<MailIcon className="w-4 h-4" data-id="element-1230" />} data-id="element-1229">
            Attach Files
          </Button>
        </div>
      </Drawer>
    </div>;
};